import React from 'react';
import { CustomDropdown } from '../common/index.js';
import { Table } from '../table/index.js';
import { DocumentsReadyForReviewChart, DocumentActivityChart, UserActivityOverTimeChart, UserActivityByStaffChart } from './ChartComponents.js';
import { UserActionLabels } from './userActionEnum.js';
import { formatSnapshotTimestamp } from './ChartComponents.js';
import MetricCard from './MetricCard.js';
import TableSectionHeader from './TableSectionHeader.js';
import { AggregationModeDropdown, ChartDownloadButton, ChartHeader, chartFileSlug } from './ChartCardControls.js';
import { buildUserActivityOverTimeSeries } from './chartSeries.js';
import {
  downloadActivityOverTimeCSV,
  downloadReadyForReviewCSV,
  downloadUserActivityOverTimeCSV,
} from './insightsCsvExport.js';

const InsightsTasksSection = ({
  aggregationMode,
  byStaffDropdownOpen,
  combinedTaskMetrics,
  combinedTaskStaffSelection,
  dateRangeText,
  focusedAction,
  focusedActionDropdown,
  getActionTitle,
  getExpandedUserIds,
  getUserSelectionText,
  handleDownloadStaffSpreadsheet,
  handleDownloadTeamSpreadsheet,
  handleFocusedActionChange,
  handleToggleAction,
  incompleteTasks,
  insightsType,
  selectedActions,
  selectedTaskTeamRows,
  setByStaffDropdownOpen,
  setSelectedTaskStaffRows,
  setSelectedTaskTeamRows,
  setTaskStaffTableDisabledFilters,
  setTaskStaffTableFilterValues,
  setTaskStaffTablePaginationState,
  setTaskStaffTableSortState,
  setTaskTeamTableDisabledFilters,
  setTaskTeamTableFilterValues,
  setTaskTeamTablePaginationState,
  setTaskTeamTableSortState,
  showUserCharts,
  staffSpreadsheetDisabled,
  taskData,
  taskStaffAggregateData,
  taskStaffTableColumns,
  taskStaffTableDisabledFilters,
  taskStaffTableFilterValues,
  taskStaffTablePaginationState,
  taskStaffTableSortState,
  taskTeamAggregateData,
  taskTeamTableColumns,
  taskTeamTableDisabledFilters,
  taskTeamTableFilterValues,
  taskTeamTablePaginationState,
  taskTeamTableSortState,
  tasksAssigned,
  tasksNeverOpened,
  tasksReadyDropdown,
  tasksReadyOptions,
  teamSpreadsheetDisabled,
  userActionOptions,
  userIdToName,
  viewMode
}) => {
  const filteredUserIds = getExpandedUserIds();
  const overTimeSeries = buildUserActivityOverTimeSeries({
    data: taskData,
    selectedAction: focusedAction,
    userIdToName,
    filteredUserIds,
    mode: aggregationMode.mode,
  });

  return (
  <>
            {insightsType === 'tasks' && viewMode === 'gp-practice' && (
              <>
                <div className="insights-metrics">
                  <div className="metrics-group">
                    <MetricCard
                      value={incompleteTasks}
                      label="Incomplete tasks"
                      dateRange={formatSnapshotTimestamp(new Date().toISOString())}
                    />
                    <MetricCard
                      value={tasksNeverOpened}
                      label="Tasks never opened"
                      dateRange={formatSnapshotTimestamp(new Date().toISOString())}
                    />
                    <MetricCard
                      value={tasksAssigned}
                      label="Tasks assigned"
                      dateRange={formatSnapshotTimestamp(new Date().toISOString())}
                    />
                  </div>
                </div>

                <div className="insights-container">
                  <div className="insights-column">
                    <div className="chart-card">
                      <ChartHeader title="Incomplete Tasks">
                        <CustomDropdown
                          options={tasksReadyOptions}
                          selectedOption={tasksReadyDropdown.selected.display}
                          onSelect={(option) => {
                            tasksReadyDropdown.handleSelect(option);
                          }}
                          isOpen={tasksReadyDropdown.isOpen}
                          toggleDropdown={() => tasksReadyDropdown.toggle([])}
                          keyExtractor={(option, index) => `tasks-ready-${option.value || index}`}
                        />
                        <ChartDownloadButton
                          disabled={!taskData?.length}
                          onClick={() => downloadReadyForReviewCSV(
                            taskData,
                            tasksReadyDropdown.selected.value,
                            'incomplete_tasks'
                          )}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        Chart that illustrates how incomplete tasks have changed in the selected time window
                      </div>
                      <div className="chart-content">
                        <DocumentsReadyForReviewChart data={taskData} type={tasksReadyDropdown.selected.value} />
                      </div>
                    </div>

                    {showUserCharts && (
                      <div className="chart-card">
                        <ChartHeader title={`${getActionTitle(focusedAction)} Over Time`}>
                          <AggregationModeDropdown
                            mode={aggregationMode.mode}
                            isOpen={aggregationMode.overTimeOpen}
                            onToggle={() => {
                              aggregationMode.toggleOverTime();
                              focusedActionDropdown.setIsOpen(false);
                            }}
                            onSelect={aggregationMode.select}
                          />
                          <CustomDropdown
                            options={userActionOptions}
                            selectedOption={UserActionLabels[focusedAction]}
                            onSelect={handleFocusedActionChange}
                            isOpen={focusedActionDropdown.isOpen}
                            toggleDropdown={() => {
                              focusedActionDropdown.toggle([]);
                              aggregationMode.close();
                            }}
                            keyExtractor={(option, index) => `user-activity-time-${option.value || index}`}
                          />
                          <ChartDownloadButton
                            disabled={overTimeSeries.userNames.length === 0}
                            onClick={() => downloadUserActivityOverTimeCSV({
                              data: taskData,
                              selectedAction: focusedAction,
                              userIdToName,
                              filteredUserIds,
                              mode: aggregationMode.mode,
                              filenamePrefix: `${chartFileSlug(getActionTitle(focusedAction))}_over_time`,
                            })}
                          />
                        </ChartHeader>
                        <div className="chart-subtitle-header">
                          Breakdown of selected activities for selected staff in selected time window, by staff
                        </div>
                        <div className="chart-content">
                          <UserActivityOverTimeChart 
                            data={taskData} 
                            selectedAction={focusedAction}
                            userIdToName={userIdToName}
                            filteredUserIds={filteredUserIds}
                            aggregationMode={aggregationMode.mode}
                            activeUsers={null}
                            onToggleUser={null}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="insights-column">
                    <div className="chart-card">
                      <ChartHeader title={`Task Activity (${getUserSelectionText()})`}>
                        <AggregationModeDropdown
                          mode={aggregationMode.mode}
                          isOpen={aggregationMode.activityOpen}
                          onToggle={aggregationMode.toggleActivity}
                          onSelect={aggregationMode.select}
                        />
                        <ChartDownloadButton
                          disabled={!taskData?.length || selectedActions.length === 0}
                          onClick={() => downloadActivityOverTimeCSV({
                            data: taskData,
                            actions: selectedActions,
                            userActionLabels: UserActionLabels,
                            filteredUserIds,
                            mode: aggregationMode.mode,
                            filenamePrefix: 'task_activity',
                          })}
                        />
                      </ChartHeader>
                      <div className="chart-subtitle-header">
                        Breakdown of selected activities for selected staff in selected time window, by activity type
                      </div>
                      <div className="chart-content">
                        <DocumentActivityChart
                          data={taskData}
                          allUserActions={selectedActions}
                          userActionLabels={UserActionLabels}
                          activeActions={selectedActions}
                          onToggleAction={handleToggleAction}
                          filteredUserIds={filteredUserIds}
                          aggregationMode={aggregationMode.mode}
                        />
                      </div>
                    </div>

                    {showUserCharts && (
                      <div className="chart-card">
                        <ChartHeader title={`${getActionTitle(focusedAction)} by Staff`}>
                          <CustomDropdown
                            options={userActionOptions}
                            selectedOption={UserActionLabels[focusedAction]}
                            onSelect={handleFocusedActionChange}
                            isOpen={byStaffDropdownOpen}
                            toggleDropdown={() => setByStaffDropdownOpen(!byStaffDropdownOpen)}
                            keyExtractor={(option, index) => `user-activity-staff-${option.value || index}`}
                          />
                        </ChartHeader>
                        <div className="chart-subtitle-header">
                          Aggregate of '{getActionTitle(focusedAction).toLowerCase()}' activity by staff in selected time window
                        </div>
                        <div className="chart-content">
                          <UserActivityByStaffChart 
                            data={taskData} 
                            selectedAction={focusedAction}
                            userIdToName={userIdToName}
                            filteredUserIds={filteredUserIds}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
      {viewMode === 'staff' && showUserCharts && insightsType === 'tasks' && (
        <>
          <div className="staff-metrics">
            <MetricCard 
              value={combinedTaskMetrics.totalCompleted}
              label="Total tasks completed"
              dateRange={dateRangeText}
            />
          </div>
          
          <div className="staff-table-container">
            <TableSectionHeader
              title="Staff Performance"
              onDownload={handleDownloadStaffSpreadsheet}
              disabled={staffSpreadsheetDisabled}
            />
            <Table
              data={taskStaffAggregateData}
              columns={taskStaffTableColumns}
              id="task-staff-insights-table"
              enableSelection={true}
              selectedRows={combinedTaskStaffSelection}
              onSelectionChange={setSelectedTaskStaffRows}
              getRowKey={(row) => row.userId}
              emptyMessage="No staff data available for this time period"
              sortState={taskStaffTableSortState}
              onSortChange={setTaskStaffTableSortState}
              filterValues={taskStaffTableFilterValues}
              onFilterChange={setTaskStaffTableFilterValues}
              disabledFilters={taskStaffTableDisabledFilters}
              onDisabledFiltersChange={setTaskStaffTableDisabledFilters}
              paginationState={taskStaffTablePaginationState}
              onPaginationChange={setTaskStaffTablePaginationState}
            />
          </div>

          <div className="staff-table-container">
            <TableSectionHeader
              title="Team Performance"
              onDownload={handleDownloadTeamSpreadsheet}
              disabled={teamSpreadsheetDisabled}
            />
            <Table
              data={taskTeamAggregateData}
              columns={taskTeamTableColumns}
              id="task-team-insights-table"
              enableSelection={true}
              selectedRows={selectedTaskTeamRows}
              onSelectionChange={setSelectedTaskTeamRows}
              getRowKey={(row) => row.teamId}
              emptyMessage="No team data available for this time period"
              sortState={taskTeamTableSortState}
              onSortChange={setTaskTeamTableSortState}
              filterValues={taskTeamTableFilterValues}
              onFilterChange={setTaskTeamTableFilterValues}
              disabledFilters={taskTeamTableDisabledFilters}
              onDisabledFiltersChange={setTaskTeamTableDisabledFilters}
              paginationState={taskTeamTablePaginationState}
              onPaginationChange={setTaskTeamTablePaginationState}
            />
          </div>
        </>
      )}
  </>
  );
};

export default InsightsTasksSection;
